import { useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuChevronDown } from 'react-icons/lu';
import {
  Alert,
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { z } from 'zod';

import { useBusiness } from 'src/features/auth/useAuth';
import { fieldErrorsFromApiError } from 'src/shared/api/errors';
import { ApiError } from 'src/shared/api/types';
import AppModal from 'src/shared/components/ui/AppModal';
import { todayKey } from 'src/shared/utils/datetime';
import { notify } from 'src/shared/utils/notify';
import rhfInput from 'src/shared/utils/rhfInput';
import zodResolver from 'src/shared/utils/zodResolver';
import { SOURCE_LABELS, toPayload } from './clientUtils';
import { useCreateClient, useUpdateClient } from './useClients';
import type { Client } from './types';

/**
 * The form's rules. Built per business timezone: "no future birth dates" has
 * to mean the salon's today — the browser's UTC date is already tomorrow from
 * 19:00 in Guayaquil.
 */
const makeSchema = (today: string) =>
  z.object({
    firstName: z.string().min(1, 'El nombre es obligatorio'),
    lastName: z.string().optional(),
    phone: z
      .string()
      .optional()
      .refine(
        (value) => !value || value.replace(/\D/g, '').length >= 6,
        'El teléfono necesita al menos 6 dígitos'
      ),
    email: z
      .string()
      .optional()
      .refine(
        (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
        'Ese correo electrónico no es válido'
      ),
    birthDate: z
      .string()
      .optional()
      .refine((value) => !value || value <= today, 'La fecha no puede ser futura'),
    notes: z.string().optional(),
    marketingOptIn: z.boolean(),
    source: z.enum(['walk_in', 'referral', 'social', 'online', 'other']),
  });

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

const EMPTY: FormValues = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  birthDate: '',
  notes: '',
  marketingOptIn: false,
  source: 'walk_in',
};

const fromClient = (client: Client): FormValues => ({
  firstName: client.firstName,
  lastName: client.lastName ?? '',
  phone: client.phone ?? '',
  email: client.email ?? '',
  birthDate: client.birthDate ?? '',
  notes: client.notes ?? '',
  marketingOptIn: client.marketingOptIn,
  source: client.source,
});

/**
 * Maps a save failure to something the user can act on.
 *
 * Keyed on status and `code`, never on the API's own prose: it answers in
 * English and this UI is in Spanish. A 409 is always the phone number here —
 * it is the only unique field a client has — and the server names the field, so
 * the message also lands on the input via `fieldErrorsFromApiError`.
 */
const errorMessage = (error: Error | null): string | null => {
  if (!error) return null;
  if (!(error instanceof ApiError)) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión.';
  }
  if (error.status === 409) return 'Ese teléfono ya pertenece a otra clienta.';
  if (error.status === 403) return 'No tienes permiso para hacer este cambio.';
  if (error.status === 422) return 'Revisa los datos marcados en el formulario.';
  return 'No pudimos guardar los cambios. Inténtalo de nuevo.';
};

/** Whether a profile has anything filled in past name and phone. */
const hasMoreData = (client: Client): boolean =>
  Boolean(client.email || client.birthDate || client.notes) ||
  client.marketingOptIn ||
  client.source !== 'walk_in';

interface ClientFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  /** Absent when registering; present when editing an existing profile. */
  client?: Client;
  onSaved?: (client: Client) => void;
}

/**
 * Registers or edits a client.
 *
 * Mounted only while open (by `QuickActionHost`, by the profile), so the form
 * starts from the client it was opened for and never needs resetting — and a
 * background refetch of the profile cannot wipe what is being typed.
 *
 * Name and phone are all a walk-in needs; everything else waits behind "Más
 * datos", open from the start when editing a profile that already has some.
 */
export default function ClientFormModal({ isOpen, toggle, client, onSaved }: ClientFormModalProps) {
  const create = useCreateClient();
  const update = useUpdateClient();
  const isEditing = Boolean(client);
  const mutation = isEditing ? update : create;
  const { data: business } = useBusiness();
  const today = todayKey(business?.timezone);
  const schema = useMemo(() => makeSchema(today), [today]);
  const moreId = useId();
  const [moreOpen, setMoreOpen] = useState(() => (client ? hasMoreData(client) : false));

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: client ? fromClient(client) : EMPTY,
  });

  // A problem in a folded field unfolds it: an error nobody can see is not
  // an error message.
  const showMore = moreOpen || Boolean(errors.email || errors.birthDate);

  const onSubmit = (values: FormValues) => {
    const payload = toPayload(values);
    const onSuccess = (saved: Client) => {
      notify.success(client ? 'Datos actualizados' : `${saved.fullName} quedó registrada`);
      onSaved?.(saved);
      toggle();
    };
    const onError = (error: Error) => {
      // Field-level errors the server found (a taken phone number) go back onto
      // the inputs instead of only into the banner.
      for (const [field, message] of Object.entries(fieldErrorsFromApiError(error))) {
        if (field in EMPTY) setError(field as keyof FormValues, { message });
      }
    };

    if (client) {
      update.mutate({ id: client.id, data: payload }, { onSuccess, onError });
    } else {
      create.mutate(payload, { onSuccess, onError });
    }
  };

  const apiMessage = errorMessage(mutation.error);

  return (
    <AppModal
      isOpen={isOpen}
      toggle={toggle}
      title={isEditing ? 'Editar clienta' : 'Nueva clienta'}
      backdrop="static"
      onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      footer={
        <>
          <Button color="link" type="button" onClick={toggle} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" /> Guardando…
              </>
            ) : isEditing ? (
              'Guardar'
            ) : (
              'Registrar clienta'
            )}
          </Button>
        </>
      }
    >
      {apiMessage && (
        <Alert color="danger" role="alert">
          {apiMessage}
        </Alert>
      )}

      <Row>
        <Col xs="12" md="6">
          <FormGroup>
            <Label for="firstName">Nombre *</Label>
            <Input
              id="firstName"
              autoComplete="given-name"
              autoCapitalize="words"
              invalid={Boolean(errors.firstName)}
              {...rhfInput(register('firstName'))}
            />
            {errors.firstName && <FormFeedback>{errors.firstName.message}</FormFeedback>}
          </FormGroup>
        </Col>
        <Col xs="12" md="6">
          <FormGroup>
            <Label for="lastName">Apellido</Label>
            <Input
              id="lastName"
              autoComplete="family-name"
              autoCapitalize="words"
              invalid={Boolean(errors.lastName)}
              {...rhfInput(register('lastName'))}
            />
            {errors.lastName && <FormFeedback>{errors.lastName.message}</FormFeedback>}
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label for="phone">Teléfono</Label>
        <Input
          id="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="0991234567"
          invalid={Boolean(errors.phone)}
          {...rhfInput(register('phone'))}
        />
        {errors.phone && <FormFeedback>{errors.phone.message}</FormFeedback>}
      </FormGroup>

      <button
        type="button"
        className="v-disclosure"
        aria-expanded={showMore}
        aria-controls={moreId}
        onClick={() => setMoreOpen(!showMore)}
      >
        <span className="v-disclosure__text">
          <span>Más datos</span>
          <span className="v-disclosure__hint">Correo, nacimiento, cómo llegó y notas</span>
        </span>
        <LuChevronDown className="v-disclosure__chevron" aria-hidden="true" />
      </button>

      {/* Hidden rather than unmounted, so what was typed there survives
          folding it away. */}
      <div id={moreId} hidden={!showMore} className="v-disclosure__panel">
        <Row>
          <Col xs="12" md="6">
            <FormGroup>
              <Label for="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                invalid={Boolean(errors.email)}
                {...rhfInput(register('email'))}
              />
              {errors.email && <FormFeedback>{errors.email.message}</FormFeedback>}
            </FormGroup>
          </Col>
          <Col xs="12" md="6">
            <FormGroup>
              <Label for="birthDate">Fecha de nacimiento</Label>
              <Input
                id="birthDate"
                type="date"
                max={today}
                invalid={Boolean(errors.birthDate)}
                {...rhfInput(register('birthDate'))}
              />
              {errors.birthDate && <FormFeedback>{errors.birthDate.message}</FormFeedback>}
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label for="source">¿Cómo llegó?</Label>
          <Input id="source" type="select" {...rhfInput(register('source'))}>
            {Object.entries(SOURCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="notes">Notas visibles para todo el personal</Label>
          <Input
            id="notes"
            type="textarea"
            rows={3}
            placeholder="Preferencias, forma de uña habitual…"
            {...rhfInput(register('notes'))}
          />
        </FormGroup>

        <FormGroup check className="mb-0">
          <Input id="marketingOptIn" type="checkbox" {...rhfInput(register('marketingOptIn'))} />
          <Label for="marketingOptIn" check>
            Acepta recibir recordatorios y promociones
          </Label>
        </FormGroup>
      </div>
    </AppModal>
  );
}
